/**
 * Tasks page detail drawer.
 *
 * Wraps `@ordo/ui/sheet` (Radix Dialog under the hood, right-side slide
 * pattern Tasker uses for task detail) and lazy-loads the right
 * subKind-specific sheet body. Unknown / unmapped subKinds fall
 * through to `GenericTaskDetailSheet` so every task remains actionable.
 *
 * Lazy + per-subKind code splitting means the sheet bundles do NOT ship
 * with the initial page load.
 */

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@ordo/ui/sheet'
import { Skeleton } from '@ordo/ui/skeleton'
import { type ComponentType, lazy, Suspense } from 'react'

import type { MyTaskRow } from '@/api/tasks'

import { GenericTaskDetailSheet, type TaskSheetProps } from './sheets/generic-detail'

/**
 * Static registry of every subKind → lazy sheet component. Adding a
 * new subKind: register it here + create
 * `apps/ordo/client/src/components/tasks/sheets/<slug>.tsx`.
 *
 * `Object.fromEntries` is intentional rather than `as const` because
 * `lazy()` returns a generic component type — keeping the map dynamic
 * avoids gymnastics with the suspense fallback typing.
 */
const SHEET_REGISTRY: Record<string, ComponentType<TaskSheetProps>> = {
  // Never the generic sheet: its "Mark complete" would close a USDA
  // compliance task with nothing recorded. Both school kinds route into the
  // Production Records wizard (PROD-1277); this sheet is the fallback link
  // for a row projected without shipment metadata.
  SCHOOL_CONFIRM_DELIVERY_RECEIVED: lazy(() =>
    import('./sheets/production-records-link').then((m) => ({ default: m.ProductionRecordsLinkSheet })),
  ),
  SCHOOL_COMPLETE_SERVICE: lazy(() =>
    import('./sheets/production-records-link').then((m) => ({ default: m.ProductionRecordsLinkSheet })),
  ),
  // Never the generic sheet: its "Mark complete" override would close a
  // USDA temp-log obligation (and unlock temp-log-gated orders) with no
  // readings. The sheet routes into the temperature-log workflow instead.
  CHEF_SUBMIT_TEMP_LOG: lazy(() => import('./sheets/chef-temp-log').then((m) => ({ default: m.ChefTempLogSheet }))),
}

/**
 * Pure resolver: which sheet component renders for the given subKind?
 * Returns the lazy component for known subKinds, the generic fallback
 * otherwise. Exported so tests can pin the registry without rendering.
 */
export function resolveSheetComponent(subKind: string | null): ComponentType<TaskSheetProps> {
  if (subKind && Object.prototype.hasOwnProperty.call(SHEET_REGISTRY, subKind)) {
    return SHEET_REGISTRY[subKind]
  }
  return GenericTaskDetailSheet
}

interface TaskDetailDrawerProps {
  task: MyTaskRow | null
  onClose: () => void
}

export function TaskDetailDrawer({ task, onClose }: TaskDetailDrawerProps) {
  // Pre-resolve the component so we don't call lazy() inside the
  // render branch when `task` is null (which would mount the lazy
  // boundary against undefined input).
  const SheetBody = task ? resolveSheetComponent(task.subKind) : null

  return (
    <Sheet open={task !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" size="md" className="flex flex-col">
        {task && SheetBody ? (
          <>
            <SheetHeader className="sr-only">
              <SheetTitle>{task.title}</SheetTitle>
              <SheetDescription>{task.description ?? 'Task detail'}</SheetDescription>
            </SheetHeader>
            <Suspense fallback={<TaskSheetSkeleton />}>
              <SheetBody task={task} onClose={onClose} />
            </Suspense>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}

/**
 * Skeleton shown while a lazy sheet bundle is loading. Matches the
 * top-level shape of `GenericTaskDetailSheet`: header, field grid,
 * footer actions. Exported for reuse by the rollup drawer's steps.
 */
export function TaskSheetSkeleton() {
  return (
    <div className="flex h-full flex-col gap-6" aria-busy="true" aria-label="Loading task">
      <header className="space-y-2 pr-8">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-3 w-full max-w-xs" />
      </header>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </div>
      <div className="mt-auto flex justify-end gap-2">
        <Skeleton className="h-9 w-20" />
        <Skeleton className="h-9 w-28" />
      </div>
    </div>
  )
}
